import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { navLinks, personalInfo } from '../data/portfolioData';

const C = {
  cyan: '#00f5ff',
  green: '#00ff88',
  bg: '#020409',
  gray: '#4a6275',
  white: '#e8f4f8',
  border: 'rgba(0,245,255,0.12)',
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { threshold: 0.4 }
    );
    navLinks.forEach(l => {
      const el = document.getElementById(l.href.replace('#', ''));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: scrolled ? '12px 48px' : '20px 48px',
        background: scrolled
          ? 'rgba(2,4,9,0.95)'
          : 'rgba(2,4,9,0.6)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${scrolled ? C.border : 'transparent'}`,
        transition: 'all .35s ease',
      }}>
        {/* Logo */}
        <a href="#hero" onClick={e => { e.preventDefault(); handleNav('#hero'); }}
          style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: 16, fontWeight: 900, letterSpacing: 3,
            color: C.cyan, textDecoration: 'none',
          }}>
          {personalInfo.shortName.split('.').map((part, i) => (
            <span key={i} style={{ color: i === 0 ? C.cyan : C.green }}>{part}{i === 0 ? '.' : ''}</span>
          ))}
        </a>

        {/* Desktop Links */}
        <ul style={{ display: 'flex', gap: 32, listStyle: 'none', alignItems: 'center' }}
          className="nav-desktop">
          {navLinks.map(link => {
            const isActive = active === link.href.replace('#', '');
            return (
              <li key={link.href}>
                <a href={link.href}
                  onClick={e => { e.preventDefault(); handleNav(link.href); }}
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 10, letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: isActive ? C.cyan : C.gray,
                    textDecoration: 'none',
                    transition: 'color .2s',
                    position: 'relative',
                    paddingBottom: 4,
                  }}
                  onMouseEnter={e => e.target.style.color = C.cyan}
                  onMouseLeave={e => e.target.style.color = isActive ? C.cyan : C.gray}
                >
                  {link.label}
                  {isActive && (
                    <span style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      height: 1, background: C.cyan,
                    }} />
                  )}
                </a>
              </li>
            );
          })}
        </ul>

        {/* CTA */}
        <a href="#contact"
          onClick={e => { e.preventDefault(); handleNav('#contact'); }}
          className="nav-cta-desktop"
          style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
            padding: '9px 20px', border: `1px solid ${C.cyan}`,
            color: C.cyan, background: 'transparent', cursor: 'pointer',
            transition: 'all .25s', textDecoration: 'none',
          }}
          onMouseEnter={e => { e.target.style.background = C.cyan; e.target.style.color = '#020409'; }}
          onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = C.cyan; }}
        >
          Hire Me
        </a>

        {/* Hamburger */}
        <button
          onClick={() => setMobileOpen(v => !v)}
          className="nav-hamburger"
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: C.cyan }}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: 'rgba(2,4,9,0.98)', backdropFilter: 'blur(20px)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 32,
          animation: 'fadeIn .2s ease',
        }}>
          {navLinks.map(link => (
            <a key={link.href} href={link.href}
              onClick={e => { e.preventDefault(); handleNav(link.href); }}
              style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: 22, fontWeight: 600, letterSpacing: 4,
                textTransform: 'uppercase', color: C.white, textDecoration: 'none',
                transition: 'color .2s',
              }}
              onMouseEnter={e => e.target.style.color = C.cyan}
              onMouseLeave={e => e.target.style.color = C.white}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          .nav-hamburger { display: flex !important; }
        }
      `}</style>
    </>
  );
}
